/**
 * Landing page variant: D — Workstation / Console
 * Converted from design prototype wireframe JSX.
 */
import React from "react";
import { Link } from "react-router-dom";
import { HOME_LINE } from "../public/legal-text.ts";
import {
  WfFooter, FooterCTA, FaqRow,
  PlayBlob, FakeWaveform, ScriptLines, ArrowDoodle,
} from "./primitives.tsx";

// Wireframe D — "Workstation / Console"
// The homepage IS the app. Engineering-blueprint feel. Less "marketing", more "you're already inside."

export function WireframeD() {
  return (
    <div className="wf paper no-tex" style={{ maxWidth: 1280, width: "100%", background: "#0b0d0c", color:"#d8d4c4", fontFamily: "Patrick Hand, sans-serif" }}>
      <style>{`
        .wfd { color:#d8d4c4; }
        .wfd .grid-bg {
          background-image:
            linear-gradient(rgba(216,212,196,0.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(216,212,196,0.06) 1px, transparent 1px);
          background-size: 24px 24px;
        }
        .wfd .panel { border: 1.5px solid #d8d4c4; background: rgba(216,212,196,0.04); }
        .wfd .panel-hd { display:flex; justify-content:space-between; padding: 6px 12px; border-bottom: 1px dashed #d8d4c4; }
        .wfd .label { color: rgba(216,212,196,0.65); }
        .wfd .accent { color: var(--accent); }
        .wfd .btn { background: var(--accent); color: #0b0d0c; box-shadow: 4px 4px 0 0 #d8d4c4; border-color: #d8d4c4; }
        .wfd .btn.ghost { background: transparent; color: #d8d4c4; box-shadow: 4px 4px 0 0 var(--accent); }
        .wfd .pill { background: transparent; color:#d8d4c4; border-color:#d8d4c4; }
        .wfd .thumb { background:#1a1d1c; border-color:#d8d4c4; }
        .wfd .mute { color: rgba(216,212,196,0.55); }
        .wfd .navrow { border-bottom-color: #d8d4c4; }
        .wfd .navrow nav a { color:#d8d4c4; }
        .wfd .logo .mark { background: var(--accent); border-color:#d8d4c4; color:#0b0d0c; }
        .wfd .crosshair { position: relative; }
        .wfd .crosshair::before, .wfd .crosshair::after {
          content:""; position:absolute; background:#d8d4c4;
        }
        .wfd .crosshair::before { top:-1px; left:-1px; width:10px; height:1px; }
        .wfd .crosshair::after  { top:-1px; left:-1px; width:1px; height:10px; }
      `}</style>

      <div className="wfd">
      {/* TOP BAR — terminal/console style */}
      <div className="row" style={{ alignItems:"center", justifyContent:"space-between", padding:"10px 28px", borderBottom: "1px solid rgba(216,212,196,0.2)" }}>
        <div className="row" style={{ gap: 14, alignItems:"center" }}>
          <span className="logo" style={{ fontSize: 18 }}><span className="mark" style={{ width:24, height:24, fontSize:13 }}>▶</span>TUBEFORGE</span>
        </div>
        <div className="row" style={{ gap: 14 }}>
          {["overview","manual","ledger","faq"].map(t => <span key={t} className="label" style={{ fontSize: 11 }}>{t}</span>)}
        </div>
        <div className="row" style={{ gap: 10 }}>
          <Link to="/login" className="btn ghost" style={{ fontSize: 14, padding: "8px 14px", textDecoration: "none" }}>sign in</Link>
          <Link to="/signup" className="btn" style={{ fontSize: 14, padding: "8px 14px", textDecoration: "none" }}>▶ open console</Link>
        </div>
      </div>

      {/* HERO — split: ASCII-ish console + live preview */}
      <section className="grid-bg" style={{ padding: "44px 28px 28px" }}>
        <div className="row" style={{ alignItems:"flex-start", gap: 24 }}>
          {/* left — type-y manifesto */}
          <div style={{ flex: 1.1 }}>
            <div className="label">// TUBEFORGE · BUILD A FACELESS CHANNEL WITHOUT EDITING</div>
            <h1 style={{ fontSize: 92, lineHeight: 1.0, marginTop: 14, letterSpacing: "-0.035em" }}>
              YOUR TOPIC.<br />
              OUR PIPELINE.<br />
              <span className="accent" style={{ whiteSpace:"nowrap" }}>
                PRESS
                <span aria-hidden="true" style={{ display:"inline-block", width:"0.62em", height:"0.62em", marginLeft:"0.24em", verticalAlign:"baseline", background:"var(--accent)", clipPath:"polygon(0 0, 0 100%, 100% 50%)" }} />
              </span>.
            </h1>
            {/* (B120-1; D5) the line under the headline */}
            <p data-home-line="" style={{ fontSize: 20, lineHeight: 1.45, maxWidth: 720, margin: "18px 0 0" }}>{HOME_LINE}</p>
            <div className="type" style={{ marginTop: 18, fontSize: 20, lineHeight: 1.45, maxWidth: 540 }}>
              A workbench for faceless YouTube. Pick a niche, sign off on the script, sign off on the frames, ship the MP4. Two gates between you and the model. Zero between you and the upload button.
            </div>
            <div className="row" style={{ marginTop: 24, gap: 14 }}>
              <Link to="/signup" className="btn" style={{ textDecoration: "none" }}>▶ boot up · 2 free credits</Link>
            </div>
            <div className="row" style={{ marginTop: 30, gap: 30 }}>
              {[
                ["≤ 10m", "topic → mp4"],
                ["2 gates", "script & frames"],
                ["1080p", "every plan"],
                ["24 / 7", "no freelancers"],
              ].map(([n,d]) => (
                <div key={n}>
                  <div style={{ fontFamily:"Permanent Marker", fontSize: 28, whiteSpace: "nowrap" }} className="accent">{n}</div>
                  <div className="label" style={{ marginTop: 2 }}>{d}</div>
                </div>
              ))}
            </div>
          </div>

          {/* right — live console preview */}
          <div style={{ flex: 1, marginTop: 4 }}>
            <div className="panel">
              <div className="panel-hd">
                <span className="label">CONSOLE · job/4f2c · soviet-space-dogs</span>
                <span className="label">●●● auto-running</span>
              </div>
              <div style={{ padding: 16, fontFamily:"Special Elite, monospace", fontSize: 12, lineHeight: 1.6 }}>
                <div>$ tf new --niche history --len 11m</div>
                <div className="mute">› preset loaded: history/long-form</div>
                <div className="mute">› voice: male/narrator-04</div>
                <div className="mute">› image-model: flux-1.dev</div>
                <div>$ tf script.draft</div>
                <div className="mute">› segments: 12 · words: 1,847 · est: 11:24</div>
                <div className="accent">› gate.1 [awaiting your eyes] █</div>
              </div>
              <div className="panel-hd" style={{ borderTop: "1px dashed #d8d4c4", borderBottom: "none" }}>
                <span className="label">PREVIEW · segment 04</span>
                <span className="label">11:24 · 1080p · ETA 4m</span>
              </div>
              <div style={{ padding: 14 }}>
                <div className="thumb" style={{ height: 180 }}>
                  <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", zIndex:3 }}>
                    <PlayBlob size={50} />
                  </div>
                  {/* crosshair corners */}
                  <div style={{ position:"absolute", inset: 6, border:"1px dashed rgba(216,212,196,0.5)", pointerEvents:"none", zIndex: 3 }} />
                </div>
                <FakeWaveform width={500} height={26} bars={56} seed={5} />
                <div className="row" style={{ gap: 6, marginTop: 8 }}>
                  {Array.from({ length: 12 }).map((_,i) =>
                    <div key={i} style={{ flex:1, height: 18, background: i<4? "var(--accent)" : "rgba(216,212,196,0.08)", border:"1px solid #d8d4c4" }} />
                  )}
                </div>
                <div className="row" style={{ justifyContent:"space-between", marginTop: 6 }}>
                  <span className="label">4 / 12 approved</span>
                  <span className="label accent">⏎ approve all · ⌘R regen</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <a id="how-it-works" />
      <section style={{ padding: "60px 28px", borderTop: "1px solid rgba(216,212,196,0.2)" }}>
        <div className="row" style={{ justifyContent:"space-between", alignItems:"baseline" }}>
          <h2 style={{ fontSize: 56 }}>// PIPELINE.DIAGRAM</h2>
          <div className="label">FOUR NODES · TWO GATES · ONE LOOP</div>
        </div>

        {/* nodes row */}
        <div className="row" style={{ marginTop: 36, alignItems:"stretch", gap: 0, position:"relative" }}>
          {[
            ["NODE 01","INPUT","topic + niche preset","auto"],
            ["NODE 02","SCRIPT","hook · body · CTA","GATE — you sign"],
            ["NODE 03","STORYBOARD","N segments → N frames","GATE — you sign"],
            ["NODE 04","RENDER","mp4 + captions + audio","auto"],
          ].map(([n,t,d,g], i, arr) => (
            <React.Fragment key={n}>
              <div className="panel" style={{ flex: 1, padding: 18, position:"relative" }}>
                <div className="label">{n}</div>
                <div style={{ fontFamily:"Permanent Marker", fontSize: 32, marginTop: 8 }}>{t}</div>
                <div className="type mute" style={{ marginTop: 8, fontSize: 14 }}>{d}</div>
                <div style={{ marginTop: 14, paddingTop: 8, borderTop:"1px dashed #d8d4c4", fontFamily:"Special Elite", fontSize: 11 }}>
                  <span className={g.includes("GATE")? "accent" : "mute"}>● {g}</span>
                </div>
              </div>
              {i < arr.length - 1 && (
                <div className="col" style={{ width: 50, alignItems:"center", justifyContent:"center" }}>
                  <ArrowDoodle dir="right" size={50} />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* return loop arrow */}
        <div className="row" style={{ marginTop: 22, gap: 10, alignItems:"center" }}>
          <ArrowDoodle dir="left" size={70} color="#d8d4c4" />
          <span className="label">↺ failed render auto-refunds credits · re-render from storyboard, no re-image cost</span>
        </div>
      </section>

      {/* LIVE PEEK — bigger app mock */}
      <section style={{ padding: "60px 28px", borderTop: "1px solid rgba(216,212,196,0.2)", background: "#0a0c0b" }}>
        <div className="label">// LIVE.DESK — INSPECT A RUN</div>
        <h2 style={{ fontSize: 52, marginTop: 8 }}>OPEN ANY JOB. WATCH IT MOVE.</h2>

        <div className="panel" style={{ marginTop: 24 }}>
          <div className="panel-hd">
            <div className="row" style={{ gap: 10 }}>
              <span className="label">job/4f2c · soviet-space-dogs.mp4</span>
            </div>
            <div className="row" style={{ gap: 14 }}>
              <span className="label">elapsed 03:42</span>
              <span className="label">eta 04:11</span>
            </div>
          </div>
          <div className="row" style={{ alignItems:"stretch" }}>
            {/* timeline panel */}
            <div style={{ width: 230, borderRight: "1px dashed #d8d4c4", padding: 14 }}>
              <div className="label">SEGMENTS · 12</div>
              <div className="col" style={{ gap: 4, marginTop: 8 }}>
                {Array.from({ length: 12 }).map((_,i) =>
                  <div key={i} className="row" style={{ alignItems:"center", gap: 6, fontSize: 12, fontFamily:"Special Elite" }}>
                    <span style={{ width: 16 }}>{String(i+1).padStart(2,"0")}</span>
                    <div style={{ flex: 1, height: 10, background: i<5? "var(--accent)" : i===5 ? "#d8d4c4" : "rgba(216,212,196,0.15)", border:"1px solid #d8d4c4" }} />
                    <span className="label" style={{ fontSize: 10 }}>{i<5 ? "✓" : i===5 ? "···" : "—"}</span>
                  </div>
                )}
              </div>
            </div>
            {/* script panel */}
            <div style={{ flex: 1, padding: 14, borderRight:"1px dashed #d8d4c4" }}>
              <div className="row" style={{ justifyContent:"space-between" }}>
                <span className="label">SCRIPT · seg 06</span>
                <span className="label">✎ edit · ↻ regen line</span>
              </div>
              <div className="type" style={{ marginTop: 12, fontSize: 16, lineHeight: 1.5 }}>
                <p>In 1957, the Soviet space program loaded a stray dog into a metal sphere the size of a washing machine and shot her into orbit. <span style={{ background:"var(--accent)", color:"#0b0d0c", padding:"0 4px" }}>her name was Laika.</span></p>
                <p className="mute">She would never come back. The program knew this. The world did not.</p>
                <div style={{ marginTop: 12 }}><ScriptLines lines={3} /></div>
              </div>
            </div>
            {/* preview panel */}
            <div style={{ width: 360, padding: 14 }}>
              <div className="row" style={{ justifyContent:"space-between" }}>
                <span className="label">FRAME · seg 06</span>
                <span className="label">↻ regen · ✎ edit prompt</span>
              </div>
              <div className="thumb" style={{ marginTop: 12, height: 180 }}>
                <div style={{ position:"absolute", inset: 6, border:"1px dashed rgba(216,212,196,0.5)", zIndex: 3 }} />
              </div>
              <div className="row" style={{ gap: 6, marginTop: 10, flexWrap:"wrap" }}>
                {["cinematic","low-key","cold tones","wide shot"].map(t => <span key={t} className="pill" style={{ fontSize: 10 }}>{t}</span>)}
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* FAQ */}
      <a id="faq" />
      <section style={{ padding: "60px 28px", borderTop: "1px solid rgba(216,212,196,0.2)", background:"#0a0c0b" }}>
        <div className="label">// MANUAL.FAQ</div>
        <h2 style={{ fontSize: 52, marginTop: 8 }}>READ.ME</h2>
        <div className="row" style={{ marginTop: 24, gap: 30 }}>
          <div style={{ flex: 1 }}>
            <FaqRow q="› how long does one run take, really?" open />
            <FaqRow q="› can i bring my own voice / channel style?" />
            <FaqRow q="› what happens when render.fail()?" />
            <FaqRow q="› do you do shorts (9:16)?" />
            <FaqRow q="› teams / agency / multiple channels?" />
            <FaqRow q="› cancel?" />
          </div>
          <div className="panel" style={{ width: 280, padding: 16, height:"fit-content" }}>
            <div className="label">› terminal still empty?</div>
            <div className="hand" style={{ fontSize: 26, marginTop: 6 }}>boot up. 2 free credits.</div>
            <Link to="/signup" className="btn" style={{ marginTop: 14, fontSize: 14, textDecoration: "none" }}>▶ start free</Link>
          </div>
        </div>
      </section>

      <FooterCTA headline="$ tf new" sub="2 free credits queued · no card · run as many as you want until they're gone." />
      <WfFooter />
      </div>
    </div>
  );
}
